// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file BaseTypeahead.tsx
 * @input Uses React, StyleX, usePopover, TypeaheadItem
 * @output Exports BaseTypeahead combobox engine component
 * @position Core implementation; used by Typeahead and Tokenizer
 *
 * Pure combobox engine: input, search, keyboard navigation, dropdown.
 * No wrapper div, no border styling, no token rendering.
 * Consumers provide their own wrapper and pass anchorRef for dropdown positioning.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Typeahead/index.ts
 * - /packages/cli/templates/blocks/components/Typeahead/ (showcase blocks)
 */
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { usePopover } from "../Popover/usePopover.js";
import { useAnnounce } from "../hooks/useAnnounce.js";
import { TypeaheadItem } from "./TypeaheadItem.js";
import { Icon } from "../Icon/index.js";
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, typographyVars, fontWeightVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { getKey, mergeProps, mergeRefs } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Types
// =============================================================================
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Styles
// =============================================================================

const styles = {
  input: {
    k1xSpc: "x1lliihq",
    kUk6DE: "x98rzlu",
    k7Eaqz: "xfvyar9",
    kMzoRj: "xc342km",
    ksu8eU: "xng3xce",
    kmVPX3: "x1717udv",
    kMv6JI: "x9ynric",
    kGuDYH: "xjm74w1 x6pjikd",
    kLWn49: "xw6l6zx",
    kMwMTN: "x1tgivj0",
    kWkggS: "xjbqb8w",
    kI3sdo: "x1a2a7pz",
    k8Qsv1: "xeyghm5",
    $$css: true
  },
  inputDisabled: {
    kkrTdU: "x1h6gzvc",
    $$css: true
  },
  popover: {
    k7Eaqz: "xrzjruh",
    $$css: true
  },
  popoverGap: {
    keoZOQ: "xcsaf9d",
    k1K539: "x14cgwvg",
    $$css: true
  },
  item: {
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kzqmXN: "xh8yej3",
    kmVPX3: "xlsj2fj",
    kaIpWk: "xh6dtrn",
    kkrTdU: "x1ypdohk",
    kI3sdo: "x1a2a7pz",
    kWkggS: "xjbqb8w",
    k9WMMc: "xdpxx8g",
    $$css: true
  },
  itemHighlighted: {
    kWkggS: "x1lmrjuc",
    $$css: true
  },
  itemSelected: {
    k63SB2: "x1e4wzip",
    $$css: true
  }
};

/**
 * Size-specific overrides for dropdown list items.
 * Matches the pattern used by DropdownMenuItem / Selector so that
 * an `sm` typeahead renders compact list items.
 */
const itemSizeStyles = {
  sm: {
    k8WAf4: "xu0wf1k",
    kg3NbH: "xf314gf",
    $$css: true
  },
  md: {
    k8WAf4: "x1vofgu7",
    $$css: true
  },
  lg: {
    k8WAf4: "xce4md1",
    $$css: true
  }
};

// =============================================================================
// Component
// =============================================================================

/**
 * Combobox engine: input, search, keyboard navigation, and dropdown.
 *
 * Renders only the `<input>` and the dropdown popover. No wrapper div,
 * no border styling, no token rendering. Consumers (Typeahead,
 * Tokenizer) provide their own wrapper and pass `anchorRef` for
 * dropdown positioning.
 *
 * @example
 * ```
 * <BaseTypeahead
 *   searchSource={source}
 *   value={selected}
 *   onChange={setSelected}
 *   anchorRef={wrapperRef}
 *   placeholder="Search..."
 * />
 * ```
 */
export const BaseTypeahead = function BaseTypeahead({
  searchSource,
  value,
  onChange,
  renderItem,
  placeholder: placeholderFromProps,
  hasEntriesOnFocus = false,
  maxMenuItems = 10,
  emptySearchResultsText: emptySearchResultsTextFromProps,
  isDisabled = false,
  isFocusableDisabled = false,
  hasAutoFocus = false,
  onChangeQuery,
  onOpenChange,
  inputId: externalInputId,
  ariaDescribedBy,
  ariaLabelledBy,
  inputXStyle,
  anchorRef,
  onKeyDown: externalOnKeyDown,
  debounceMs = 150,
  size = 'md',
  ref
}) {
  const t = useTranslator();
  const placeholder = placeholderFromProps ?? t('@astryx.typeahead.searchPlaceholder');
  const emptySearchResultsText = emptySearchResultsTextFromProps ?? t('@astryx.typeahead.emptySearchResults');
  const generatedId = useId();
  const inputId = externalInputId ?? generatedId;
  const listboxId = useId();
  const inputRef = useRef(null);
  const fallbackAnchorRef = useRef(null);

  // Announce result counts / "no results" to screen readers via a persistent
  // live region (comboboxes-6). The combobox's own popup carries no working
  // live region, so highlight/result changes were previously silent.
  const announce = useAnnounce();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Track active pointer to defer popover.show() past click events.
  // With popover="auto", showing the popover between pointerdown and
  // pointerup/click causes the browser's light-dismiss to immediately
  // close it (the click is seen as "outside" the newly-opened popover).
  const pointerActiveRef = useRef(false);

  // Debounce ref
  const searchTimeoutRef = useRef(null);

  // Monotonic counter incremented on selection and query-clear. Async
  // searches that resolve after a selection compare their captured
  // generation to the current value and discard stale results.
  const searchGenRef = useRef(0);
  // The generation at which results were last populated. handleFocus
  // compares this to searchGenRef — if they differ, the cached results
  // in the closure are stale (a selection cleared them) and shouldn't
  // be re-shown.
  const resultsGenRef = useRef(0);

  // Layer for dropdown
  const handleLayerShow = useCallback(() => {
    onOpenChange?.(true);
  }, [onOpenChange]);
  const handleLayerHide = useCallback(() => {
    onOpenChange?.(false);
    setHighlightedIndex(-1);
    searchSource.cancel?.();
  }, [onOpenChange, searchSource]);
  const popover = usePopover({
    onShow: handleLayerShow,
    onHide: handleLayerHide,
    hasLightDismiss: true,
    hasCloseButton: false,
    hasAutoFocus: false,
    // The popup's own role="listbox" is the exposed semantics; the input keeps
    // DOM focus, so wrapping it in a modal dialog would misrepresent it.
    role: 'none'
  });

  // Show the layer, deferring past the active click if a pointer is down.
  // Without this, popover="auto" light-dismiss immediately closes the
  // dropdown when it opens between pointerdown and pointerup/click.
  const showLayer = useCallback(() => {
    if (pointerActiveRef.current) {
      document.addEventListener('click', () => requestAnimationFrame(() => popover.show()), {
        once: true
      });
    } else {
      popover.show();
    }
  }, [popover]);

  // Set up anchor on the provided anchorRef or fall back to the input itself
  useEffect(() => {
    const el = anchorRef?.current ?? fallbackAnchorRef.current;
    if (el) {
      popover.triggerRef(el);
    }
    return () => {
      popover.triggerRef(null);
    };
  }, [popover, anchorRef]);

  // Perform search
  const performSearch = useCallback(async searchQuery => {
    searchSource.cancel?.();
    // Claim a new generation so overlapping searches can't race: an
    // in-flight response for an older query fails the gen check below
    // instead of overwriting the newer results.
    const gen = ++searchGenRef.current;
    setIsLoading(true);
    setHasSearched(true);
    try {
      const searchResults = await searchSource.search(searchQuery);
      if (searchGenRef.current !== gen) {
        return;
      }
      resultsGenRef.current = gen;
      const shown = searchResults.slice(0, maxMenuItems);
      setResults(shown);
      setHighlightedIndex(searchResults.length > 0 ? 0 : -1);
      if (searchResults.length > 0 || searchQuery.length > 0) {
        showLayer();
      }
      // Announce the outcome only for an active query (not the initial
      // focus-open), so screen-reader users hear result counts / no-results.
      if (searchQuery.length > 0) {
        announce(shown.length === 0 ? emptySearchResultsText : `${shown.length} ${shown.length === 1 ? 'result' : 'results'}`);
      }
    } catch {
      if (searchGenRef.current !== gen) {
        return;
      }
      setResults([]);
      setHighlightedIndex(-1);
    } finally {
      if (searchGenRef.current === gen) {
        setIsLoading(false);
      }
    }
  }, [searchSource, maxMenuItems, showLayer, announce, emptySearchResultsText]);

  // Perform bootstrap
  const performBootstrap = useCallback(async () => {
    const gen = ++searchGenRef.current;
    setIsLoading(true);
    try {
      const bootstrapResults = await searchSource.bootstrap();
      if (searchGenRef.current !== gen) {
        return;
      }
      resultsGenRef.current = gen;
      setResults(bootstrapResults.slice(0, maxMenuItems));
      setHighlightedIndex(bootstrapResults.length > 0 ? 0 : -1);
      if (bootstrapResults.length > 0) {
        showLayer();
      }
    } catch {
      if (searchGenRef.current !== gen) {
        return;
      }
      setResults([]);
    } finally {
      if (searchGenRef.current === gen) {
        setIsLoading(false);
      }
    }
  }, [searchSource, maxMenuItems, showLayer]);

  // Handle query change
  const handleQueryChange = useCallback(newQuery => {
    setQuery(newQuery);
    onChangeQuery?.(newQuery);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (newQuery.length === 0 && !hasEntriesOnFocus) {
      searchGenRef.current++;
      searchSource.cancel?.();
      setResults([]);
      setHasSearched(false);
      // Clear any lingering result-count / no-results announcement.
      announce('');
      popover.hide();
      return;
    }
    const triggerSearch = () => {
      if (newQuery.length > 0) {
        void performSearch(newQuery);
      } else if (hasEntriesOnFocus) {
        void performBootstrap();
      }
    };
    if (debounceMs <= 0) {
      triggerSearch();
    } else {
      searchTimeoutRef.current = setTimeout(triggerSearch, debounceMs);
    }
  }, [onChangeQuery, hasEntriesOnFocus, performSearch, performBootstrap, popover, debounceMs, searchSource, announce]);

  // Handle input change
  const handleInputChange = useCallback(e => {
    handleQueryChange(e.target.value);
  }, [handleQueryChange]);

  // Handle item selection
  const handleSelect = useCallback(item => {
    // Bump generation to invalidate any in-flight async searches
    searchGenRef.current++;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    searchSource.cancel?.();
    onChange(item);
    setQuery('');
    setResults([]);
    setHasSearched(false);
    popover.hide();
    inputRef.current?.focus();
  }, [onChange, popover, searchSource]);

  // Handle focus
  const handleFocus = useCallback(() => {
    if (isDisabled) {
      return;
    }
    if (hasEntriesOnFocus && results.length === 0 && query.length === 0) {
      void performBootstrap();
    } else if (results.length > 0 && (query.length > 0 || hasEntriesOnFocus) &&
    // Only re-show cached results if they haven't been invalidated by
    // a selection. Refs are always current, so this check isn't affected
    // by React's closure staleness the way results.length is.
    resultsGenRef.current === searchGenRef.current) {
      showLayer();
    }
  }, [isDisabled, hasEntriesOnFocus, results.length, query.length, performBootstrap, showLayer]);

  // Handle blur — close the dropdown when focus leaves the input for an
  // element that is neither inside the field wrapper (anchor) nor inside the
  // dropdown popover. The native popover="auto" light-dismiss only fires on
  // outside pointer clicks and Escape; it does not close when focus moves away
  // via the keyboard (Tab) or programmatically, which would otherwise leave an
  // orphaned open menu. Clicking a result moves focus onto the option (it is
  // tabIndex={-1}, so it lives inside the popover) and selection re-focuses the
  // input, so this only closes on a genuine focus-out of the whole field.
  const handleBlur = useCallback(e => {
    if (!popover.isOpen) {
      return;
    }
    const next = e.relatedTarget;
    if (next) {
      const anchorEl = anchorRef?.current ?? fallbackAnchorRef.current;
      const popoverEl = document.getElementById(popover.id);
      if (anchorEl?.contains(next) || popoverEl?.contains(next)) {
        return;
      }
    }
    popover.hide();
  }, [popover, anchorRef]);

  // Keyboard navigation
  const handleKeyDown = useCallback(e => {
    externalOnKeyDown?.(e);
    if (e.defaultPrevented) {
      return;
    }
    if (!popover.isOpen) {
      if (e.key === 'ArrowDown' && (hasEntriesOnFocus || query.length > 0)) {
        e.preventDefault();
        if (results.length > 0) {
          popover.show();
          setHighlightedIndex(0);
        } else if (hasEntriesOnFocus) {
          void performBootstrap();
        }
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < results.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : results.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < results.length) {
          handleSelect(results[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        popover.hide();
        break;
      case 'Home':
        if (popover.isOpen) {
          e.preventDefault();
          setHighlightedIndex(0);
        }
        break;
      case 'End':
        if (popover.isOpen) {
          e.preventDefault();
          setHighlightedIndex(results.length - 1);
        }
        break;
    }
  }, [popover, results, highlightedIndex, handleSelect, hasEntriesOnFocus, query.length, performBootstrap, externalOnKeyDown]);

  // Generate item ID for accessibility
  const getItemId = useCallback(index => `${listboxId}-option-${index}`, [listboxId]);

  // Keep the highlighted option visible during keyboard navigation. The
  // listbox is a fixed-height scroll container, so without this the virtual
  // cursor walks off-screen once navigation passes the visible window. Mirrors
  // CommandPaletteItem's scrollIntoView({block: 'nearest'}) behavior.
  useEffect(() => {
    if (!popover.isOpen || highlightedIndex < 0) {
      return;
    }
    document.getElementById(getItemId(highlightedIndex))?.scrollIntoView?.({
      block: 'nearest'
    });
  }, [popover.isOpen, highlightedIndex, getItemId]);
  const selectedKey = value == null ? null : getKey(value.id, () => results.indexOf(value));

  // Cleanup timeout and cancel in-flight searches on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchSource.cancel?.();
    };
  }, [searchSource]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("input", {
      ref: mergeRefs(ref, inputRef, fallbackAnchorRef),
      id: inputId,
      type: "text",
      role: "combobox",
      "aria-expanded": popover.isOpen,
      "aria-controls": listboxId,
      "aria-activedescendant": popover.isOpen && highlightedIndex >= 0 ? getItemId(highlightedIndex) : undefined,
      "aria-autocomplete": "list",
      "aria-describedby": ariaDescribedBy,
      "aria-labelledby": ariaLabelledBy,
      "aria-disabled": isFocusableDisabled ? 'true' : undefined,
      value: query,
      onChange: handleInputChange,
      onPointerDown: () => {
        pointerActiveRef.current = true;
        document.addEventListener('click', () => {
          pointerActiveRef.current = false;
        }, {
          once: true
        });
      },
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      placeholder: placeholder
      // When a disabled-reason tooltip is shown the input keeps focusability
      // via aria-disabled + readOnly instead of the native disabled
      // attribute; value mutation stays blocked by the isDisabled guards.
      ,
      disabled: isDisabled && !isFocusableDisabled,
      readOnly: isFocusableDisabled || undefined,
      autoFocus: hasAutoFocus,
      "data-autofocus": hasAutoFocus || undefined,
      autoComplete: "off",
      ...stylex.props(styles.input, isDisabled && styles.inputDisabled, inputXStyle)
    }), isLoading && /*#__PURE__*/_jsx("span", {
      role: "status",
      "aria-label": t('@astryx.typeahead.loading'),
      ...{
        className: "x3nfvp2 x6s0dn4 xl56j7k x9epnlk"
      },
      children: /*#__PURE__*/_jsx(Icon, {
        icon: "clock",
        size: "sm",
        color: "secondary"
      })
    }), popover.render(/*#__PURE__*/_jsx("div", {
      id: listboxId,
      role: "listbox",
      "aria-label": t('@astryx.typeahead.searchResults'),
      ...mergeProps(themeProps('typeahead-dropdown'), {
        className: "x9f619 xuyqlj2 x1odjw0f x9epnlk"
      }),
      children: results.length === 0 && hasSearched ? /*#__PURE__*/_jsx("div", {
        ...{
          className: "x1b2ylru x2b8uid x141an7d xv1l7n4"
        },
        children: emptySearchResultsText
      }) : results.map((item, index) => {
        const itemKey = getKey(item.id, index);
        const isSelected = itemKey === selectedKey;
        return /*#__PURE__*/_jsxs("div", {
          id: getItemId(index),
          role: "option",
          "aria-selected": isSelected,
          tabIndex: -1,
          onClick: () => handleSelect(item),
          onMouseEnter: () => setHighlightedIndex(index),
          ...stylex.props(styles.item, itemSizeStyles[size], index === highlightedIndex && styles.itemHighlighted, isSelected && styles.itemSelected),
          children: [/*#__PURE__*/_jsx("span", {
            ...{
              className: "x78zum5 x98rzlu xeuugli"
            },
            children: renderItem ? renderItem(item) : /*#__PURE__*/_jsx(TypeaheadItem, {
              item: item
            })
          }), isSelected && /*#__PURE__*/_jsx(Icon, {
            icon: "check",
            size: "sm",
            color: "primary"
          })]
        }, itemKey);
      })
    }), {
      placement: 'below',
      alignment: 'start',
      xstyle: [styles.popover, styles.popoverGap]
    })]
  });
};
BaseTypeahead.displayName = 'BaseTypeahead';